import './i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from "react";
import {
  Award,
  BarChart3,
  Bell,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Crown,
  Flame,
  History,
  Loader2,
  Medal,
  RefreshCw,
  Search,
  Shield,
  Sparkles,
  Star,
  Target,
  Trophy,
  User,
  Users,
  Zap,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Bar,
  BarChart,
} from "recharts";

// -----------------------------------------------------
// Config
// -----------------------------------------------------
const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5000";

function getAccessToken() {
  return localStorage.getItem("access_token") || localStorage.getItem("token") || "";
}

async function fetchJson(path, { signal } = {}) {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || data?.detail || `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function clamp(n, min = 0, max = 100) {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  return Math.max(min, Math.min(max, x));
}

function formatNumber(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat().format(Number.isFinite(n) ? n : 0);
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
}

function prettyLabel(v) {
  if (!v) return "—";
  return String(v)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function safeArray(v) {
  if (Array.isArray(v)) return v;
  if (v?.items && Array.isArray(v.items)) return v.items;
  return [];
}

function userName(user) {
  const email = user?.email || "Player";
  const first = user?.first_name || user?.firstName;
  const last = user?.last_name || user?.lastName;
  const full = [first, last].filter(Boolean).join(" ").trim();
  return full || email.split("@")[0] || "Player";
}

function initials(name) {
  return String(name || "U")
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0])
    .join("")
    .toUpperCase();
}

const rankTiers = [
  { name: "Beginner", min: 1, max: 5, icon: Shield, color: "from-slate-300 to-white" },
  { name: "Novice", min: 6, max: 11, icon: Medal, color: "from-emerald-300 to-white" },
  { name: "Experienced", min: 12, max: 20, icon: Star, color: "from-sky-300 to-white" },
  { name: "Skilled", min: 21, max: 30, icon: Trophy, color: "from-cyan-300 to-emerald-200" },
  { name: "Master", min: 31, max: 40, icon: Crown, color: "from-white to-sky-200" },
  { name: "Champion", min: 41, max: 999, icon: Trophy, color: "from-emerald-300 to-sky-300" },
];

function tierForLevel(level, rankName) {
  if (rankName) {
    const byName = rankTiers.find((x) => x.name.toLowerCase() === String(rankName).toLowerCase());
    if (byName) return byName;
  }
  return rankTiers.find((x) => Number(level || 1) >= x.min && Number(level || 1) <= x.max) || rankTiers[0];
}

function xpProgress(user) {
  const xp = Number(user?.xp || 0);
  const next = Number(user?.next_level_xp || 1000);
  const previousLevelTarget = Math.max(0, (Number(user?.level || 1) - 1) * 1000);
  const within = Math.max(0, xp - previousLevelTarget);
  const needed = Math.max(1, next - previousLevelTarget);
  return {
    xp,
    next,
    within,
    needed,
    pct: clamp((within / needed) * 100),
    remaining: Math.max(0, next - xp),
  };
}

const AppBackground = ({ children }) => (
  <div className="min-h-screen w-full bg-[#07090c] text-white relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[80px]" />
      <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[90px]" />
      <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
      <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/70" />
    </div>
    <div className="relative">{children}</div>
  </div>
);

function SoftCard({ children, className = "" }) {
  return (
    <div
      className={cn(
        "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
        className
      )}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, className = "", type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition",
        "shadow-[0_12px_30px_-18px_rgba(255,255,255,0.50)] hover:bg-white/90 disabled:opacity-50 disabled:hover:bg-white",
        className
      )}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, disabled, className = "", type = "button" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white/85 transition",
        "hover:bg-white/8 disabled:opacity-50",
        className
      )}
    >
      {children}
    </button>
  );
}

function Pill({ children, tone = "neutral", className = "" }) {
  const cls =
    tone === "emerald"
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
      : tone === "sky"
      ? "border-sky-400/20 bg-sky-400/10 text-sky-100"
      : tone === "white"
      ? "border-white/18 bg-white/10 text-white"
      : "border-white/12 bg-white/6 text-white/80";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide",
        cls,
        className
      )}
    >
      {children}
    </span>
  );
}

function TopBar() {
  return (
    <div className="flex items-center justify-between px-6 py-5 mt-20">
      <button
        type="button"
        onClick={() => { window.location.href = "/dashboard-home"; }}
        className="group inline-flex items-center gap-2"
        title="InterviewGenieAI"
      >
        <div className="h-9 w-9 rounded-2xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center">
          <Sparkles className="h-4.5 w-4.5 text-white/85" />
        </div>
        <div className="leading-tight text-left">
          <div className="text-sm font-semibold tracking-tight">Gamified Progress</div>
          <div className="text-[11px] text-white/55 -mt-0.5">XP • Leaderboard • Achievements</div>
        </div>
      </button>

      <div className="hidden md:flex items-center gap-2">
        <Pill tone="emerald">Live XP</Pill>
        <Pill tone="sky">Ranking</Pill>
        <Pill>Progress</Pill>
      </div>
    </div>
  );
}

function Avatar({ name, size = "md", className = "" }) {
  const sizeMap = {
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm",
    lg: "h-14 w-14 text-base",
    xl: "h-20 w-20 text-xl",
  };
  return (
    <div
      className={cn(
        "grid place-items-center rounded-2xl bg-gradient-to-br from-emerald-300/95 via-sky-200/90 to-white text-zinc-950 font-black shadow-[0_10px_35px_-15px_rgba(56,189,248,0.65)] ring-1 ring-white/40",
        sizeMap[size],
        className
      )}
    >
      {initials(name)}
    </div>
  );
}

function ProgressBar({ value, max = 100, className = "" }) {
  const pct = max > 0 ? clamp((Number(value || 0) / Number(max || 1)) * 100) : 0;
  return (
    <div className={cn("h-2.5 w-full rounded-full bg-white/10 overflow-hidden", className)}>
      <div
        className="h-full rounded-full bg-gradient-to-r from-emerald-300 via-sky-300 to-white transition-all duration-700 shadow-[0_0_24px_rgba(56,189,248,0.35)]"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-zinc-950/95 ring-1 ring-white/10 px-3 py-2 shadow-xl backdrop-blur-xl">
      {label != null ? <div className="text-xs text-white/60 mb-1">{label}</div> : null}
      <div className="space-y-1">
        {payload.map((p, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm text-white">
            <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.stroke || p.fill }} />
            <span className="text-white/60">{p.name}:</span>
            <span className="font-semibold tabular-nums">{p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub, accent = "from-emerald-400/15 via-white/[0.035] to-transparent" }) {
  return (
    <SoftCard className="relative overflow-hidden p-4">
      <div className={cn("absolute inset-0 bg-gradient-to-br", accent)} />
      <div className="relative flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-white/[0.06] ring-1 ring-white/10 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-white/80" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-white/45">{label}</div>
          <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{value}</div>
          {sub ? <div className="mt-1 text-xs text-white/55 truncate">{sub}</div> : null}
        </div>
      </div>
    </SoftCard>
  );
}

function EmptyBlock({ icon: Icon = History, title, text }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-center backdrop-blur-2xl">
      <div className="mx-auto h-11 w-11 rounded-xl bg-white/[0.05] ring-1 ring-white/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-white/65" />
      </div>
      <div className="mt-3 font-semibold text-white">{title}</div>
      <div className="mt-1 text-sm text-white/55">{text}</div>
    </div>
  );
}

function normalizeLeaderboardRows(leaderboard, currentUserId) {
  return safeArray(leaderboard).map((row, idx) => ({
    id: row.user_id || row.id || idx + 1,
    place: Number(row.rank || idx + 1),
    name: row.name || row.email || `Player ${idx + 1}`,
    xp: Number(row.xp || 0),
    level: Number(row.level || 1),
    rank: row.rank_title || row.rank || "Beginner",
    streak: Number(row.streak || row.current_streak || 0),
    isCurrentUser: String(row.user_id || row.id) === String(currentUserId),
  }));
}

function normalizeActivity(activity) {
  const data = activity?.activity_data || activity?.metadata || {};
  return {
    id: activity?.id || `${activity?.type}-${activity?.created_at}`,
    title: activity?.title || prettyLabel(activity?.type || "Activity"),
    description: activity?.description || (data?.score ? `Score: ${data.score}%` : "Activity recorded"),
    score: activity?.score ?? data?.score,
    xp: data?.xp_amount || activity?.xp_amount,
    type: activity?.type,
    createdAt: activity?.created_at || activity?.timestamp,
  };
}

function AchievementCard({ achievement }) {
  const unlocked = Boolean(achievement?.unlocked);
  const progress = achievement?.progress;
  let pct = unlocked ? 100 : 0;
  if (progress && typeof progress === "object") {
    const current = Number(progress.current ?? progress.value ?? progress.count ?? 0);
    const target = Number(progress.target ?? progress.total ?? progress.required ?? 0);
    if (target > 0) pct = clamp((current / target) * 100);
  }

  return (
    <div
      className={cn(
        "rounded-2xl p-4 ring-1 transition",
        unlocked
          ? "bg-emerald-400/[0.08] ring-emerald-400/25 shadow-[0_18px_45px_-30px_rgba(16,185,129,0.65)]"
          : "bg-white/[0.03] ring-white/10"
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "h-11 w-11 rounded-xl flex items-center justify-center ring-1 shrink-0",
            unlocked
              ? "bg-gradient-to-br from-emerald-300 to-sky-300 ring-emerald-200/40 text-zinc-950"
              : "bg-white/[0.05] ring-white/10 text-white/55"
          )}
        >
          {achievement?.icon_url ? (
            <img src={achievement.icon_url} alt="" className="h-6 w-6 object-contain" />
          ) : unlocked ? (
            <Trophy className="h-5 w-5" />
          ) : (
            <Shield className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold text-white truncate">{achievement?.name || "Achievement"}</div>
            <span
              className={cn(
                "text-[11px] px-2 py-1 rounded-full shrink-0",
                unlocked
                  ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/25"
                  : "bg-white/5 text-white/45 ring-1 ring-white/10"
              )}
            >
              {unlocked ? "Unlocked" : "Locked"}
            </span>
          </div>
          <p className="mt-1 text-sm text-white/55 line-clamp-2">{achievement?.description || "Complete more interviews to unlock this reward."}</p>
          <div className="mt-3 flex items-center justify-between gap-3 text-xs text-white/45">
            <span>{Number(achievement?.xp_reward || 0)} XP reward</span>
            <span>{Math.round(pct)}%</span>
          </div>
          <div className="mt-2"><ProgressBar value={pct} max={100} /></div>
          {achievement?.unlocked_at ? <div className="mt-2 text-[11px] text-emerald-100/70">Unlocked {formatDate(achievement.unlocked_at)}</div> : null}
        </div>
      </div>
    </div>
  );
}

function LeaderboardTable({ rows, query }) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.name} ${r.rank} ${r.level}`.toLowerCase().includes(q));
  }, [rows, query]);

  if (!filtered.length) {
    return <EmptyBlock title="No players found" text="Try another search keyword." icon={Users} />;
  }

  return (
    <div className="overflow-hidden rounded-2xl ring-1 ring-white/10">
      <div className="grid grid-cols-[70px_1fr_100px_120px_110px] gap-3 bg-white/[0.04] px-4 py-3 text-xs uppercase tracking-wide text-white/40 max-md:hidden">
        <div>Rank</div><div>Player</div><div>Level</div><div>Streak</div><div className="text-right">XP</div>
      </div>
      <div className="divide-y divide-white/10">
        {filtered.map((row) => {
          const tier = tierForLevel(row.level, row.rank);
          const Icon = tier.icon;
          return (
            <div
              key={`${row.id}-${row.place}`}
              className={cn(
                "grid grid-cols-[70px_1fr_100px_120px_110px] gap-3 px-4 py-3 items-center transition max-md:grid-cols-[52px_1fr] max-md:gap-2",
                row.isCurrentUser ? "bg-emerald-400/[0.08]" : "hover:bg-white/[0.03]"
              )}
            >
              <div className="flex items-center gap-2">
                <span className={cn("h-8 w-8 rounded-xl flex items-center justify-center font-bold", row.place <= 3 ? "bg-white text-zinc-950" : "bg-white/5 text-white/65")}>#{row.place}</span>
              </div>
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={row.name} size="sm" />
                <div className="min-w-0">
                  <div className="font-semibold text-white truncate">{row.name}</div>
                  <div className="text-xs text-white/45 flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {prettyLabel(row.rank)}</div>
                </div>
              </div>
              <div className="text-sm text-white/70 max-md:hidden">Level {row.level}</div>
              <div className="text-sm text-white/70 max-md:hidden inline-flex items-center gap-1"><Flame className="h-4 w-4 text-emerald-300" /> {row.streak} days</div>
              <div className="text-right text-sm font-semibold text-white tabular-nums max-md:col-span-2 max-md:text-left max-md:pl-[52px]">{formatNumber(row.xp)} XP</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopPodium({ rows }) {
  const top = rows.slice(0, 3);
  if (!top.length) return <EmptyBlock title="Leaderboard is empty" text="Complete interviews to start ranking." icon={Trophy} />;
  const order = [top[1], top[0], top[2]].filter(Boolean);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
      {order.map((row) => {
        const place = row.place;
        const height = place === 1 ? "md:min-h-[230px]" : place === 2 ? "md:min-h-[200px]" : "md:min-h-[180px]";
        const tier = tierForLevel(row.level, row.rank);
        const Icon = tier.icon;
        return (
          <SoftCard key={row.id} className={cn("relative overflow-hidden p-5", height, place === 1 && "ring-emerald-400/25 bg-emerald-400/[0.07]") }>
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 via-sky-400/[0.04] to-transparent" />
            <div className="relative flex flex-col items-center text-center">
              <div className={cn("rounded-full px-3 py-1 text-xs font-bold", place === 1 ? "bg-white text-zinc-950" : "bg-white/10 text-white/70")}>#{place}</div>
              <div className="mt-4 relative">
                <Avatar name={row.name} size={place === 1 ? "xl" : "lg"} />
                <div className="absolute -right-2 -bottom-2 h-8 w-8 rounded-xl bg-zinc-950 ring-1 ring-white/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-emerald-200" />
                </div>
              </div>
              <div className="mt-4 text-lg font-semibold text-white">{row.name}</div>
              <div className="text-sm text-white/55">{prettyLabel(row.rank)} • Level {row.level}</div>
              <div className="mt-3 text-2xl font-black text-white tabular-nums">{formatNumber(row.xp)}</div>
              <div className="text-xs text-white/45">total XP</div>
            </div>
          </SoftCard>
        );
      })}
    </div>
  );
}

export default function Gamified() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState("overview");
  const [query, setQuery] = useState("");
  const [profile, setProfile] = useState(null);
  const [leaderboardRaw, setLeaderboardRaw] = useState([]);
  const [achievementsRes, setAchievementsRes] = useState(null);
  const [activitiesRes, setActivitiesRes] = useState(null);
  const [statsRes, setStatsRes] = useState(null);
  const [dashRes, setDashRes] = useState(null);
  const [achievementStats, setAchievementStats] = useState(null);

  async function loadData({ silent = false } = {}) {
    const ctrl = new AbortController();
    if (!silent) setLoading(true);
    setRefreshing(silent);
    setError("");
    try {
      const [profileData, leaderboardData, achievementsData, activitiesData, statsData, dashboardData, achStatsData] = await Promise.all([
        fetchJson("/api/gamification/gamification-profile", { signal: ctrl.signal }),
        fetchJson("/api/gamification/leaderboard", { signal: ctrl.signal }).catch(() => []),
        fetchJson("/api/gamification/achievements", { signal: ctrl.signal }).catch(() => null),
        fetchJson("/api/gamification/activities", { signal: ctrl.signal }).catch(() => null),
        fetchJson("/api/interview/statistics", { signal: ctrl.signal }).catch(() => null),
        fetchJson("/api/interview/analytics/dashboard", { signal: ctrl.signal }).catch(() => null),
        fetchJson("/api/interview/analytics/achievement-stats", { signal: ctrl.signal }).catch(() => null),
      ]);
      setProfile(profileData || null);
      setLeaderboardRaw(Array.isArray(leaderboardData) ? leaderboardData : leaderboardData?.leaderboard || []);
      setAchievementsRes(achievementsData || null);
      setActivitiesRes(activitiesData || null);
      setStatsRes(statsData || null);
      setDashRes(dashboardData || null);
      setAchievementStats(achStatsData || null);
    } catch (e) {
      if (e?.name !== "AbortError") setError(e?.message || "Failed to load gamification data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    return () => ctrl.abort();
  }

  useEffect(() => {
    loadData();
  }, []);

  const user = profile?.user || {};
  const name = userName(user);
  const tier = tierForLevel(user?.level, user?.rank);
  const TierIcon = tier.icon;
  const progress = xpProgress(user);
  const leaderboard = useMemo(() => normalizeLeaderboardRows(leaderboardRaw, user?.id), [leaderboardRaw, user?.id]);
  const currentRank = leaderboard.find((x) => x.isCurrentUser)?.place || leaderboard.findIndex((x) => String(x.id) === String(user?.id)) + 1 || "—";

  const achievements = useMemo(() => {
    const fromDedicated = safeArray(achievementsRes?.achievements);
    const fromProfile = safeArray(profile?.achievements);
    return fromDedicated.length ? fromDedicated : fromProfile;
  }, [achievementsRes, profile]);

  const totalUnlocked = achievementsRes?.total_unlocked ?? achievements.filter((a) => a.unlocked).length;
  const totalAvailable = achievementsRes?.total_available ?? achievements.length;
  const activities = useMemo(() => safeArray(activitiesRes?.activities).map(normalizeActivity), [activitiesRes]);
  const recentXpEvents = safeArray(profile?.recent_xp_events);
  const tokenUsage = safeArray(profile?.token_usage);
  const weeklyData = safeArray(dashRes?.weekly_data);
  const skillsData = safeArray(dashRes?.skills_data).map((x) => ({ name: x.name, value: clamp(x.value) }));
  const stats = statsRes?.statistics || {};

  const achievementProgressCards = [
    { label: "Interview Ace", value: achievementStats?.interview_ace ?? 0, icon: Trophy },
    { label: "Consistency King", value: achievementStats?.consistency_king ?? 0, icon: Flame },
    { label: "Technical Master", value: achievementStats?.technical_master ?? 0, icon: Target },
  ];

  return (
    <AppBackground>
      <TopBar />
      <div className="mx-auto max-w-7xl px-6 pb-12 pt-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <Pill tone="emerald">
              <Sparkles className="h-3.5 w-3.5" />
              InterviewGenieAI Gamification
            </Pill>
            <h1 className="mt-3 text-[34px] leading-[1.05] sm:text-[46px] font-semibold tracking-tight">
              Rankings, achievements
              <span className="text-white/60"> and XP signal.</span>
            </h1>
            <p className="mt-3 max-w-2xl text-base text-white/65 leading-relaxed">Real-time profile, leaderboard, streaks, achievements, and interview performance signals.</p>
          </div>

          <div className="flex flex-wrap gap-3 lg:flex-nowrap lg:items-center">
            <div className="flex flex-1 min-w-[220px] sm:min-w-[300px] items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 backdrop-blur-xl">
              <Search className="h-4 w-4 text-white/55 shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search leaderboard or achievements…"
                className="w-full bg-transparent text-sm text-white placeholder:text-white/35 outline-none"
              />
            </div>
            <GhostButton onClick={() => loadData({ silent: true })} className="px-4 py-2">
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </GhostButton>
          </div>
        </div>

        {loading ? (
          <SoftCard className="mt-8 p-6 flex items-center gap-3 text-white/70"><Loader2 className="h-5 w-5 animate-spin text-sky-200" /> Loading gamification data…</SoftCard>
        ) : error ? (
          <SoftCard className="mt-8 p-5">
            <div className="font-semibold text-white">Couldn’t load gamification</div>
            <div className="mt-1 text-sm text-white/60">{error}</div>
            <div className="mt-3 text-xs text-white/40">Check your JWT token and that {API_BASE} is reachable.</div>
          </SoftCard>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SoftCard className="relative overflow-hidden p-6 lg:col-span-2">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/15 via-white/[0.035] to-transparent" />
                <div className="relative grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-5 md:items-center">
                  <div className="relative">
                    <Avatar name={name} size="xl" />
                    <div className="absolute -right-2 -bottom-2 h-10 w-10 rounded-2xl bg-zinc-950 ring-1 ring-white/10 flex items-center justify-center">
                      <TierIcon className="h-5 w-5 text-emerald-200" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 text-emerald-100 px-3 py-1 text-xs font-semibold">{prettyLabel(user?.rank || tier.name)}</span>
                      <span className="rounded-full bg-white/5 text-white/55 ring-1 ring-white/10 px-3 py-1 text-xs">{prettyLabel(user?.plan || "Free")} plan</span>
                    </div>
                    <h2 className="mt-3 text-2xl font-semibold text-white truncate">{name}</h2>
                    <p className="mt-1 text-sm text-white/55 truncate">{user?.email || "Authenticated player"}</p>
                    <div className="mt-5 max-w-xl">
                      <div className="mb-2 flex items-center justify-between text-xs text-white/55">
                        <span>{formatNumber(progress.xp)} XP</span>
                        <span>{formatNumber(progress.next)} next level</span>
                      </div>
                      <ProgressBar value={progress.within} max={progress.needed} />
                      <div className="mt-2 text-xs text-sky-100/75">{Math.round(progress.pct)}% complete • {formatNumber(progress.remaining)} XP remaining</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-1 gap-3">
                    <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-4 py-3 text-center">
                      <div className="text-xs text-white/45">Level</div>
                      <div className="text-3xl font-black text-white">{user?.level || 1}</div>
                    </div>
                    <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-4 py-3 text-center">
                      <div className="text-xs text-white/45">Global Rank</div>
                      <div className="text-3xl font-black text-white">{currentRank === "—" ? "—" : `#${currentRank}`}</div>
                    </div>
                  </div>
                </div>
              </SoftCard>

              <SoftCard className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-white">Rank ladder</div>
                    <div className="mt-1 text-sm text-white/55">Your current progression tier</div>
                  </div>
                  <Crown className="h-5 w-5 text-emerald-200" />
                </div>
                <div className="mt-4 space-y-2">
                  {rankTiers.map((t) => {
                    const Icon = t.icon;
                    const activeTier = t.name === tier.name;
                    return (
                      <div key={t.name} className={cn("rounded-xl p-3 ring-1 flex items-center gap-3", activeTier ? "bg-emerald-400/[0.08] ring-emerald-400/25" : "bg-white/[0.025] ring-white/10") }>
                        <div className={cn("h-9 w-9 rounded-xl flex items-center justify-center bg-gradient-to-br text-zinc-950", t.color)}><Icon className="h-4 w-4" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-white">{t.name}</div>
                          <div className="text-xs text-white/45">Levels {t.min}–{t.max === 999 ? "+" : t.max}</div>
                        </div>
                        {activeTier ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : null}
                      </div>
                    );
                  })}
                </div>
              </SoftCard>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile icon={Zap} label="Total XP" value={formatNumber(user?.xp)} sub={`${formatNumber(progress.remaining)} XP to level up`} accent="from-emerald-400/15 via-white/[0.035] to-transparent" />
              <StatTile icon={Flame} label="Current streak" value={`${user?.current_streak || 0}d`} sub={`Insurance: ${user?.streak_insurance || 0}`} accent="from-sky-400/15 via-white/[0.035] to-transparent" />
              <StatTile icon={Award} label="Achievements" value={`${totalUnlocked}/${totalAvailable || 0}`} sub="Unlocked rewards" accent="from-emerald-400/15 via-white/[0.035] to-transparent" />
              <StatTile icon={BarChart3} label="Avg interview score" value={stats?.average_score != null ? Math.round(stats.average_score) : "—"} sub={`${stats?.completed_interviews || 0} completed interviews`} accent="from-sky-400/15 via-white/[0.035] to-transparent" />
            </div>

            <div className="mt-6 flex flex-wrap gap-2 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-2 backdrop-blur-xl">
              {[
                ["overview", "Overview"],
                ["leaderboard", "Leaderboard"],
                ["achievements", "Achievements"],
                ["activity", "Activity"],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setActive(key)} className={cn("rounded-xl px-4 py-2 text-sm font-medium transition", active === key ? "bg-white text-zinc-950 shadow-[0_12px_30px_-18px_rgba(255,255,255,0.50)]" : "text-white/65 hover:bg-white/[0.05]")}>{label}</button>
              ))}
            </div>

            {active === "overview" ? (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SoftCard className="p-5 lg:col-span-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-semibold text-white">Weekly performance</div>
                      <div className="mt-1 text-sm text-white/55">Score and sessions from your interview analytics</div>
                    </div>
                    <div className="text-xs text-white/50">Trend: {dashRes?.weekly_trend != null ? `${dashRes.weekly_trend > 0 ? "+" : ""}${dashRes.weekly_trend}%` : "—"}</div>
                  </div>
                  <div className="mt-4 h-[280px]">
                    {weeklyData.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={weeklyData} margin={{ left: 8, right: 8, top: 10, bottom: 0 }}>
                          <defs><linearGradient id="signalScore" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="rgba(52,211,153,0.30)" /><stop offset="95%" stopColor="rgba(125,211,252,0.02)" /></linearGradient></defs>
                          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                          <XAxis dataKey="day" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }} tickLine={false} axisLine={false} />
                          <YAxis domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 12 }} tickLine={false} axisLine={false} />
                          <Tooltip content={<ChartTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }} iconType="circle" />
                          <Area type="monotone" dataKey="score" name="Avg score" stroke="rgba(125,211,252,0.95)" fill="url(#signalScore)" strokeWidth={2} isAnimationActive={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : <EmptyBlock title="No weekly data yet" text="Complete interviews to fill this chart." icon={BarChart3} />}
                  </div>
                </SoftCard>

                <SoftCard className="p-5">
                  <div className="flex items-center justify-between"><div><div className="font-semibold text-white">Skill radar</div><div className="mt-1 text-sm text-white/55">Average skill categories</div></div><Target className="h-5 w-5 text-emerald-200" /></div>
                  <div className="mt-4 h-[280px]">
                    {skillsData.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={skillsData} outerRadius="78%">
                          <PolarGrid stroke="rgba(255,255,255,0.08)" />
                          <PolarAngleAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 11 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }} axisLine={false} />
                          <Tooltip content={<ChartTooltip />} />
                          <Radar name="Score" dataKey="value" stroke="rgba(125,211,252,0.95)" fill="rgba(52,211,153,0.20)" strokeWidth={2} isAnimationActive={false} />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : <EmptyBlock title="No skill data" text="Skill analytics appear after completed interviews." icon={Target} />}
                  </div>
                </SoftCard>

                <SoftCard className="p-5 lg:col-span-2">
                  <div className="font-semibold text-white">Achievement momentum</div>
                  <div className="mt-1 text-sm text-white/55">Progress from real interview analytics</div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {achievementProgressCards.map(({ label, value, icon: Icon }) => (
                      <div key={label} className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-4">
                        <div className="flex items-center gap-2 text-white/70"><Icon className="h-4 w-4 text-emerald-200" /><span className="text-sm font-semibold">{label}</span></div>
                        <div className="mt-4 text-3xl font-black text-white">{Math.round(value)}%</div>
                        <div className="mt-3"><ProgressBar value={value} max={100} /></div>
                      </div>
                    ))}
                  </div>
                </SoftCard>

                <SoftCard className="p-5">
                  <div className="font-semibold text-white">Token usage</div>
                  <div className="mt-1 text-sm text-white/55">Recent AI usage events</div>
                  <div className="mt-4 space-y-2">
                    {tokenUsage.length ? tokenUsage.map((u, idx) => (
                      <div key={u.id || idx} className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3 flex items-center justify-between gap-3">
                        <div><div className="text-sm text-white/80">{formatNumber(u.tokens || u.tokens_used || 0)} tokens</div><div className="text-xs text-white/45">{formatDate(u.created_at)}</div></div>
                        <Zap className="h-4 w-4 text-emerald-200" />
                      </div>
                    )) : <div className="text-sm text-white/50">No token usage yet.</div>}
                  </div>
                </SoftCard>
              </div>
            ) : null}

            {active === "leaderboard" ? (
              <div className="mt-6 space-y-4">
                <TopPodium rows={leaderboard} />
                <SoftCard className="p-5">
                  <div className="mb-4 flex items-center justify-between"><div><div className="font-semibold text-white">Global leaderboard</div><div className="mt-1 text-sm text-white/55">Top users ordered by XP</div></div><Users className="h-5 w-5 text-emerald-200" /></div>
                  <LeaderboardTable rows={leaderboard} query={query} />
                </SoftCard>
              </div>
            ) : null}

            {active === "achievements" ? (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SoftCard className="p-5 lg:col-span-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div><div className="font-semibold text-white">Achievements</div><div className="mt-1 text-sm text-white/55">Unlocked and locked rewards from your backend</div></div>
                    <div className="text-sm text-white/60">{totalUnlocked}/{totalAvailable || 0} unlocked</div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {achievements.length ? achievements.filter((a) => `${a.name} ${a.description}`.toLowerCase().includes(query.toLowerCase())).map((a) => <AchievementCard key={a.id} achievement={a} />) : <div className="xl:col-span-3 md:col-span-2"><EmptyBlock title="No achievements configured" text="Seed achievements in the backend to show them here." icon={Award} /></div>}
                  </div>
                </SoftCard>
              </div>
            ) : null}

            {active === "activity" ? (
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SoftCard className="p-5 lg:col-span-2">
                  <div className="font-semibold text-white">Recent activity</div>
                  <div className="mt-1 text-sm text-white/55">Latest interview and XP events</div>
                  <div className="mt-4 space-y-2">
                    {activities.length ? activities.map((a) => (
                      <div key={a.id} className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-4 flex items-start gap-3">
                        <div className="h-10 w-10 rounded-xl bg-white/[0.05] ring-1 ring-white/10 flex items-center justify-center shrink-0"><History className="h-4 w-4 text-emerald-200" /></div>
                        <div className="min-w-0 flex-1"><div className="font-semibold text-white truncate">{a.title}</div><div className="mt-1 text-sm text-white/55">{a.description}</div><div className="mt-2 text-xs text-white/35">{formatDate(a.createdAt)}</div></div>
                        {a.score != null ? <div className="text-right"><div className="text-lg font-bold text-white">{Math.round(a.score)}</div><div className="text-xs text-white/40">score</div></div> : null}
                      </div>
                    )) : <EmptyBlock title="No activity yet" text="Complete an interview to generate activity." icon={History} />}
                  </div>
                </SoftCard>
                <SoftCard className="p-5">
                  <div className="font-semibold text-white">Recent XP events</div>
                  <div className="mt-1 text-sm text-white/55">Last awards from gamification profile</div>
                  <div className="mt-4 space-y-2">
                    {recentXpEvents.length ? recentXpEvents.map((e, idx) => (
                      <div key={e.id || idx} className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-3">
                        <div className="flex items-center justify-between gap-3"><div className="text-sm font-semibold text-white">+{formatNumber(e.xp_amount || e.xp || 0)} XP</div><Sparkles className="h-4 w-4 text-emerald-200" /></div>
                        <div className="mt-1 text-xs text-white/45">{prettyLabel(e.source || e.type || "XP event")}</div>
                        <div className="mt-1 text-xs text-white/30">{formatDate(e.created_at)}</div>
                      </div>
                    )) : <div className="text-sm text-white/50">No XP events yet.</div>}
                  </div>
                </SoftCard>
              </div>
            ) : null}
          </>
        )}
      </div>
    </AppBackground>
  );
}
